'use client'

import { useCallback, useMemo, useState } from 'react'
import Button from '@components/Furry/Button'
import { useTranslation } from 'react-i18next'
import SimpleSpinner from '@components/SimpleSpinner'
import { Holding } from '@hooks/useHoldings'
import { StakedListing } from './HoldingDetails.type'
import useStaking from '@hooks/useStaking'
import { useAsync } from 'react-async-hook'
import {
  auctionManagerKey,
  claimedMetadataKey,
  init as initAuctionManager,
} from '@dewicats/auction-manager-sdk'
import { useSolana } from '@dewicats/connect-button'
import { DEWICATS_COLLECTION } from '@utils/constants'

type ClaimAllStakingRewardsButtonProps = {
  key?: string
  listings: StakedListing[]
  holding: Holding
  onClaimAll?: () => void
}

const ClaimAllStakingRewardsButton = ({
  key,
  listings,
  holding,
  onClaimAll,
}: ClaimAllStakingRewardsButtonProps) => {
  const [claiming, setClaiming] = useState(false)
  const { t } = useTranslation()
  const { anchorProvider } = useSolana()

  const {
    result: claimMetadata,
    loading: claimMetadataLoading,
    execute: executeClaimMetadata,
  } = useAsync(async () => {
    if (!anchorProvider) {
      return null
    }

    const auctionManagerProgram = await initAuctionManager(anchorProvider)
    const [auctionKey] = auctionManagerKey(DEWICATS_COLLECTION, 'dewicats')
    const [claimMetadataK] = claimedMetadataKey(holding.publicKey, auctionKey)

    try {
      const claimMetadata =
        await auctionManagerProgram.account.claimedMetadataV0.fetch(
          claimMetadataK,
        )
      return claimMetadata
    } catch (error) {
      return null
    }
  }, [anchorProvider])

  const { batchClaimRewards, getStakeReceipt } = useStaking({
    nft: holding.publicKey,
  })

  const { result: auctionProgram } = useAsync(() => {
    return initAuctionManager(anchorProvider)
  }, [anchorProvider])

  const { result: stakeReceipts, execute: executeStakeReceipt } =
    useAsync(async () => {
      return auctionProgram.account.stakeRecieptV0.all([
        { memcmp: { offset: 8, bytes: holding.publicKey.toBase58() } },
      ])
    }, [getStakeReceipt, listings])

  const unClaimedListings = useMemo(() => {
    if (!listings || !stakeReceipts || claimMetadataLoading) return []

    if (claimMetadata) {
      return listings
        .filter(
          (listing) =>
            listing.createdAt.toNumber() >
              claimMetadata.lastClaimAt.toNumber() &&
            listing.totalStakedCount.toNumber() > 0,
        )
        .map((listing) => ({
          nftName: holding.name,
          listing: listing.publicKey,
          tokenMint: listing.tokenMint,
          endAt: listing.endAt.toNumber(),
        }))
    }

    return listings
      .filter(
        (listing) =>
          !stakeReceipts.find(
            (receipt) =>
              receipt.account.listing.toBase58() ===
              listing.publicKey.toBase58(),
          ),
      )
      .map((listing) => ({
        nftName: holding.name,
        listing: listing.publicKey,
        tokenMint: listing.tokenMint,
        endAt: listing.endAt.toNumber(),
      }))
  }, [
    listings,
    stakeReceipts,
    holding.name,
    claimMetadataLoading,
    claimMetadata,
  ])

  const handleClaimAllRewards = useCallback(async () => {
    try {
      setClaiming(true)

      await batchClaimRewards(unClaimedListings, onClaimAll)
      await executeStakeReceipt()
      await executeClaimMetadata()
    } catch {
    } finally {
      setClaiming(false)
    }
  }, [
    batchClaimRewards,
    unClaimedListings,
    onClaimAll,
    executeStakeReceipt,
    executeClaimMetadata,
  ])

  if (unClaimedListings.length === 0 || claimMetadataLoading) return null

  return (
    <Button
      key={key}
      ripple
      className={`text-xs px-8 text-right ${claiming ? 'pointer-events-none' : ''}`}
      onClick={handleClaimAllRewards}
    >
      {claiming ? (
        <SimpleSpinner size={'small'} />
      ) : (
        t('holdings.details.claimAll')
      )}
    </Button>
  )
}

export default ClaimAllStakingRewardsButton
